import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { UserRole } from '@anchor-comms/shared';
import { api } from '@/lib/api';

export interface SessionUser {
  id: string;
  email: string;
  name: string;
  role: UserRole;
}

interface BetterAuthSessionResponse {
  user: {
    id: string;
    email: string;
    name: string;
    role?: UserRole;
  } | null;
  session: unknown | null;
}

export function useSession() {
  return useQuery({
    queryKey: ['session'],
    queryFn: async (): Promise<SessionUser | null> => {
      try {
        const data = await api<BetterAuthSessionResponse>('/api/v1/auth/get-session');
        if (!data?.user) return null;
        return {
          id: data.user.id,
          email: data.user.email,
          name: data.user.name,
          role: data.user.role ?? 'rep',
        };
      } catch {
        return null;
      }
    },
    staleTime: 60_000,
  });
}

export function useSignIn() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { email: string; password: string }) =>
      api<unknown>('/api/v1/auth/sign-in/email', { method: 'POST', body: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['session'] }),
  });
}

export function useSignOut() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<unknown>('/api/v1/auth/sign-out', { method: 'POST', body: {} }),
    onSuccess: () => {
      qc.setQueryData(['session'], null);
      qc.invalidateQueries({ queryKey: ['session'] });
    },
  });
}
